import { useMemo } from 'react'

import { ErrorSurface } from '@/components/error-surface'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Badge } from '@/components/ui/badge'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { formatDateTime } from '@/lib/datetime'

import { SNAPSHOT_STATUS_TONE, SNAPSHOT_TYPE_LABEL, resolveBranchLabel } from './constants'
import { useReportSnapshot, useReportsBranchReach } from './queries'
import { RestrictedPanel } from './restricted-panel'
import { REPORTS_PERMISSIONS, useReportsPermissions } from './permissions'

/**
 * One frozen snapshot, read back with the integrity hash that proves the stored figures have not
 * moved since they were written.
 *
 * `payloadJson` is rendered verbatim rather than parsed into one of this module's own report
 * shapes — see `StoredReport`'s own note in `reports-types.ts` on why: re-projecting it through
 * today's response types is exactly how a stored figure quietly acquires this quarter's shape, and
 * the hash is a hash of the bytes as written, not of whatever this client might reconstruct from
 * them.
 */
export function SnapshotDetailSection({ snapshotId }: { snapshotId: string }) {
  const permissions = useReportsPermissions()
  const reach = useReportsBranchReach()
  const query = useReportSnapshot(snapshotId, permissions.canBrowseSnapshots)

  const pretty = useMemo(() => {
    if (!query.data) return ''
    try {
      return JSON.stringify(JSON.parse(query.data.payloadJson), null, 2)
    } catch {
      return query.data.payloadJson
    }
  }, [query.data])

  if (!permissions.canBrowseSnapshots) {
    return <RestrictedPanel what="Report snapshots" permission={REPORTS_PERMISSIONS.trialBalanceView} />
  }

  if (query.isPending) {
    return <Skeleton className="h-96 w-full" />
  }

  if (query.isError) {
    return <ErrorSurface error={query.error} onRetry={() => void query.refetch()} title="This snapshot could not be read" />
  }

  const { snapshot, payloadHash } = query.data

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <div className="min-w-0">
            <CardTitle>{SNAPSHOT_TYPE_LABEL[snapshot.reportType]}</CardTitle>
            <p className="mt-0.5 text-xs text-muted">
              {snapshot.fromDate !== null && snapshot.toDate !== null
                ? `${snapshot.fromDate} to ${snapshot.toDate}`
                : `As of ${snapshot.asOf}`}
              {' · '}
              {snapshot.branchId === null ? 'Whole institution' : resolveBranchLabel(reach.branches, snapshot.branchId)}
              {' · '}
              {snapshot.dimension}
            </p>
          </div>
          <Badge tone={SNAPSHOT_STATUS_TONE[snapshot.status]}>{snapshot.status}</Badge>
        </CardHeader>
        <CardContent className="space-y-3 p-4">
          {snapshot.supersededBy !== null ? (
            <Alert tone="info">
              <AlertTitle>Superseded</AlertTitle>
              <AlertDescription>
                A later rebuild replaced this snapshot for the same report, date, period and scope.
                Both are kept — this one is the figure that was reported at the time.
              </AlertDescription>
            </Alert>
          ) : null}

          {snapshot.status === 'Partial' ? (
            <Alert tone="warning">
              <AlertTitle>This snapshot is partial</AlertTitle>
              <AlertDescription>
                At least one source could not be read when this was built — see the sources below.
                Nothing in it may be quoted as an institutional figure.
              </AlertDescription>
            </Alert>
          ) : null}

          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <Stat label="Rows" value={String(snapshot.rowCount)} />
            <Stat label="Currency" value={snapshot.currencyCode} />
            <Stat label="Generated" value={formatDateTime(snapshot.generatedAtUtc)} />
            <Stat
              label="Generated by"
              value={snapshot.generatedByUserId === null ? 'A scheduled job' : `User ${snapshot.generatedByUserId}`}
            />
          </div>

          {snapshot.classificationSchemeCode !== null ? (
            <p className="text-xs text-muted">
              Classification scheme: <span className="font-mono">{snapshot.classificationSchemeCode}</span>
            </p>
          ) : null}

          {snapshot.sources.length > 0 ? (
            <div className="space-y-1">
              <p className="text-xs font-medium text-heading">Sources</p>
              <ul className="space-y-0.5 text-xs text-body">
                {snapshot.sources.map((source) => (
                  <li key={source.sourceName}>
                    <span className={source.isAvailable ? 'text-success-text' : 'text-danger-text'}>
                      {source.isAvailable ? 'Answered' : 'Did not answer'}
                    </span>
                    {' — '}
                    {source.sourceName}
                    {source.unavailableReason ? `: ${source.unavailableReason}` : ''}
                  </li>
                ))}
              </ul>
            </div>
          ) : null}

          <p className="text-2xs text-muted">
            Integrity hash: <span className="font-mono break-all">{payloadHash}</span>. The server refuses
            to serve this payload if it no longer matches this hash, so a stored figure read from here
            can only ever be exactly what was frozen.
          </p>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Stored payload</CardTitle>
          <p className="text-2xs text-muted">Exactly as written — not re-rendered through today's screens.</p>
        </CardHeader>
        <CardContent className="p-0">
          <pre className="max-h-[32rem] overflow-auto bg-surface-muted p-4 text-2xs text-body">{pretty}</pre>
        </CardContent>
      </Card>
    </div>
  )
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-control border border-border-subtle bg-surface p-3">
      <p className="text-2xs text-muted">{label}</p>
      <p className="text-sm font-semibold text-heading">{value}</p>
    </div>
  )
}
